import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Activity, Eye, Bell, BarChart3, RefreshCw, CheckCircle2,
  XCircle, Loader2, Zap, Globe, ExternalLink, Link2, AlertTriangle,
  Clock, Shield, BellOff, ChevronRight, Wifi, WifiOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useWebSocket } from '@/hooks/useWebSocket';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const EVENT_COLORS = {
  HotelRegistered: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  TokensMinted: 'bg-[#F7931A]/15 text-[#F7931A] border-[#F7931A]/20',
  TokensBurned: 'bg-red-500/15 text-red-400 border-red-500/20',
  OFTSent: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  OFTReceived: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  CrossChainTransferBurned: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
  Transfer_ERC20: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
  ReputationMinted: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
  ScoresUpdated: 'bg-sky-500/15 text-sky-400 border-sky-500/20',
  ReputationRevoked: 'bg-rose-500/15 text-rose-400 border-rose-500/20',
  TransferBlocked: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  PlatformAuthorized: 'bg-teal-500/15 text-teal-400 border-teal-500/20',
};

const NOTIF_ICONS = {
  hotel_registered: Globe,
  sbt_minted: Shield,
  bridge_sent: Zap,
  bridge_received: Link2,
  sbt_revoked: XCircle,
  transfer_blocked: AlertTriangle,
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function ChainHealthCard({ chainKey, data }) {
  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4" data-testid={`health-${chainKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${data.healthy ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
          <h3 className="text-sm font-semibold heading-font text-gray-200">{data.name}</h3>
        </div>
        <Badge variant="outline" className="text-[9px] border-white/10 text-gray-500 mono">
          {data.chain_id}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[10px]">
        <div className="bg-white/[0.02] rounded-lg px-3 py-2">
          <span className="text-gray-600 block">Latency</span>
          <span className={`font-medium mono ${data.latency_ms < 500 ? 'text-emerald-400' : data.latency_ms < 2000 ? 'text-amber-400' : 'text-red-400'}`}>
            {data.latency_ms}ms
          </span>
        </div>
        <div className="bg-white/[0.02] rounded-lg px-3 py-2">
          <span className="text-gray-600 block">Last Block</span>
          <span className="text-gray-300 font-medium mono">{data.last_block?.toLocaleString()}</span>
        </div>
        <div className="bg-white/[0.02] rounded-lg px-3 py-2">
          <span className="text-gray-600 block">Scanned To</span>
          <span className="text-gray-300 font-medium mono">{data.scanned_to_block?.toLocaleString()}</span>
        </div>
        <div className="bg-white/[0.02] rounded-lg px-3 py-2">
          <span className="text-gray-600 block">Events</span>
          <span className="text-gray-300 font-medium mono">{data.total_events}</span>
        </div>
      </div>
      {data.consecutive_errors > 0 && (
        <div className="mt-2 text-[10px] text-red-400 bg-red-500/5 rounded-lg px-3 py-1.5 border border-red-500/10">
          {data.consecutive_errors} consecutive errors — {data.last_error?.slice(0, 60)}
        </div>
      )}
      {data.last_poll && (
        <p className="text-[9px] text-gray-600 mt-2 text-right">Last poll: {timeAgo(data.last_poll)}</p>
      )}
    </div>
  );
}

function EventRow({ event }) {
  const color = EVENT_COLORS[event.event] || 'bg-gray-500/15 text-gray-400 border-gray-500/20';
  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`event-${event.tx_hash?.slice(0,8)}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Badge variant="outline" className={`text-[9px] shrink-0 ${color}`}>
          {event.event}
        </Badge>
        <span className="text-xs text-gray-400 truncate">{event.chain_name}</span>
        <span className="text-[10px] mono text-gray-600 hidden sm:inline">
          tx: {event.tx_hash?.slice(0, 10)}...
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-[10px] mono text-gray-600">#{event.block?.toLocaleString()}</span>
        <span className="text-[10px] text-gray-600">{timeAgo(event.timestamp)}</span>
      </div>
    </div>
  );
}

function NotificationRow({ notif }) {
  const Icon = NOTIF_ICONS[notif.type] || Bell;
  return (
    <div className={`flex items-start gap-3 py-3 px-4 rounded-lg border ${notif.read ? 'bg-white/[0.01] border-white/5' : 'bg-[#F7931A]/5 border-[#F7931A]/10'}`} data-testid={`notif-${notif.type}`}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${notif.read ? 'text-gray-600' : 'text-[#F7931A]'}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-xs ${notif.read ? 'text-gray-500' : 'text-gray-200'}`}>{notif.message}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] text-gray-600">{notif.chain_name}</span>
          <span className="text-[10px] text-gray-700">{timeAgo(notif.timestamp)}</span>
        </div>
      </div>
    </div>
  );
}

function AnalyticsBar({ label, count, max, color = 'bg-[#F7931A]' }) {
  const width = max > 0 ? Math.max(4, (count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-[10px] text-gray-500 w-32 truncate">{label}</span>
      <div className="flex-1 h-3 bg-white/[0.03] rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${width}%` }} />
      </div>
      <span className="text-[10px] mono text-gray-400 w-10 text-right">{count}</span>
    </div>
  );
}

export default function WatcherDashboardPage({ embedded = false }) {
  const [tab, setTab] = useState('health');
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [events, setEvents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, healthRes, eventsRes, notifsRes, analyticsRes] = await Promise.all([
        fetch(`${API}/api/watcher/status`),
        fetch(`${API}/api/watcher/health`),
        fetch(`${API}/api/watcher/events?limit=50`),
        fetch(`${API}/api/watcher/notifications?limit=50`),
        fetch(`${API}/api/watcher/analytics`),
      ]);
      const [s, h, e, n, a] = await Promise.all([
        statusRes.json(), healthRes.json(), eventsRes.json(), notifsRes.json(), analyticsRes.json(),
      ]);
      setStatus(s);
      setHealth(h);
      setEvents(e.events || []);
      setNotifications(n.notifications || []);
      setUnreadCount(n.unread_count || 0);
      setAnalytics(a);
    } catch (err) {
      console.error('Failed to load watcher data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // WebSocket — real-time updates
  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'event') {
      setEvents(prev => [msg.data, ...prev].slice(0, 50));
      setStatus(prev => prev ? { ...prev, total_events_detected: (prev.total_events_detected || 0) + 1 } : prev);
    } else if (msg.type === 'notification') {
      setNotifications(prev => [msg.data, ...prev].slice(0, 50));
      setUnreadCount(prev => prev + 1);
    } else if (msg.type === 'health') {
      setHealth(msg.data);
    } else if (msg.type === 'init') {
      setUnreadCount(msg.data?.unread_notifications || 0);
    }
  }, []);

  const { connected: wsConnected } = useWebSocket('/api/ws/watcher', handleWsMessage);

  // Fallback polling — 60s when WS connected, 15s when not
  useEffect(() => {
    const interval = setInterval(loadData, wsConnected ? 60000 : 15000);
    return () => clearInterval(interval);
  }, [loadData, wsConnected]);

  const markAllRead = async () => {
    try {
      await fetch(`${API}/api/watcher/notifications/read`, { method: 'POST' });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch { toast.error('Failed'); }
  };

  const maxEventType = analytics ? Math.max(...Object.values(analytics.events_by_type || {}), 1) : 1;
  const maxChainEvent = analytics ? Math.max(...Object.values(analytics.events_by_chain || {}), 1) : 1;

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#0a0e1a]'} data-testid="watcher-dashboard-page">
      {/* Nav */}
      {!embedded && (
      <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0e1a]/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/protocol" className="text-gray-400 hover:text-white transition-colors" data-testid="back-to-protocol">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
                <Eye className="w-4 h-4 text-white" />
              </div>
              <span className="heading-font text-base font-semibold tracking-tight">Blockchain Watcher</span>
            </div>
            {status?.running && (
              <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 bg-emerald-500/5 text-[9px]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse inline-block" />LIVE
              </Badge>
            )}
            <Badge variant="outline" className={`text-[9px] ${wsConnected ? 'border-cyan-500/30 text-cyan-400 bg-cyan-500/5' : 'border-gray-600/30 text-gray-600 bg-gray-600/5'}`} data-testid="ws-status">
              {wsConnected ? <Wifi className="w-2.5 h-2.5 mr-1" /> : <WifiOff className="w-2.5 h-2.5 mr-1" />}
              {wsConnected ? 'WS' : 'Polling'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Badge className="bg-[#F7931A] text-black text-[9px] h-5 px-1.5">{unreadCount}</Badge>
            )}
            <Button size="sm" variant="outline" onClick={loadData} className="border-white/10 text-gray-400 hover:text-gray-200" data-testid="refresh-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
            </Button>
          </div>
        </div>
      </nav>
      )}

      <div className={embedded ? 'px-4 lg:px-6 py-6' : 'max-w-6xl mx-auto px-4 py-8'}>
        {/* Header stats */}
        {status && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="watcher-stats">
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-[#F7931A]">{status.chains_monitored?.length || 0}</p>
              <p className="text-[10px] text-gray-500">Chains</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-gray-100">{status.total_events_detected || 0}</p>
              <p className="text-[10px] text-gray-500">Events</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-gray-100">{status.total_actions_triggered || 0}</p>
              <p className="text-[10px] text-gray-500">Actions</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-gray-100">{status.poll_interval_sec || 15}s</p>
              <p className="text-[10px] text-gray-500">Poll Interval</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
              <p className="text-lg font-bold heading-font mono text-emerald-400">11</p>
              <p className="text-[10px] text-gray-500">Event Types</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-white/[0.03] border border-white/5" data-testid="watcher-tabs">
            <TabsTrigger value="health" className="text-xs gap-1" data-testid="tab-health">
              <Activity className="w-3 h-3" />Health
            </TabsTrigger>
            <TabsTrigger value="events" className="text-xs gap-1" data-testid="tab-events">
              <Zap className="w-3 h-3" />Events
            </TabsTrigger>
            <TabsTrigger value="notifications" className="text-xs gap-1 relative" data-testid="tab-notifications">
              <Bell className="w-3 h-3" />Notifications
              {unreadCount > 0 && <span className="ml-1 bg-[#F7931A] text-black text-[8px] rounded-full w-4 h-4 flex items-center justify-center">{unreadCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="analytics" className="text-xs gap-1" data-testid="tab-analytics">
              <BarChart3 className="w-3 h-3" />Analytics
            </TabsTrigger>
          </TabsList>

          {/* Health Tab */}
          <TabsContent value="health">
            {loading ? (
              <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F7931A] mx-auto" /></div>
            ) : health ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="health-grid">
                {Object.entries(health).map(([key, data]) => (
                  <ChainHealthCard key={key} chainKey={key} data={data} />
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No health data available</p>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="space-y-2" data-testid="events-list">
              {events.length === 0 ? (
                <p className="text-gray-500 text-center py-12">No events detected yet. The watcher is scanning...</p>
              ) : (
                events.map((event, i) => <EventRow key={`${event.tx_hash}-${event.log_index}-${i}`} event={event} />)
              )}
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            {unreadCount > 0 && (
              <div className="flex justify-end mb-3">
                <Button size="sm" variant="outline" onClick={markAllRead} className="text-xs border-white/10 text-gray-400" data-testid="mark-all-read-btn">
                  <BellOff className="w-3 h-3 mr-1.5" />Mark all read
                </Button>
              </div>
            )}
            <div className="space-y-2" data-testid="notifications-list">
              {notifications.length === 0 ? (
                <p className="text-gray-500 text-center py-12">No notifications yet. Events will trigger alerts here.</p>
              ) : (
                notifications.map((notif, i) => <NotificationRow key={i} notif={notif} />)
              )}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analytics ? (
              <div className="grid sm:grid-cols-2 gap-6" data-testid="analytics-panels">
                <Card className="bg-white/[0.03] border-white/5">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-semibold heading-font text-gray-200 mb-4">Events by Type</h3>
                    <div className="space-y-2.5">
                      {Object.entries(analytics.events_by_type || {}).map(([type, count]) => (
                        <AnalyticsBar key={type} label={type} count={count} max={maxEventType} />
                      ))}
                      {Object.keys(analytics.events_by_type || {}).length === 0 && (
                        <p className="text-[10px] text-gray-600">No events recorded yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white/[0.03] border-white/5">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-semibold heading-font text-gray-200 mb-4">Events by Chain</h3>
                    <div className="space-y-2.5">
                      {Object.entries(analytics.events_by_chain || {}).map(([chain, count]) => (
                        <AnalyticsBar key={chain} label={chain} count={count} max={maxChainEvent} color="bg-purple-500" />
                      ))}
                      {Object.keys(analytics.events_by_chain || {}).length === 0 && (
                        <p className="text-[10px] text-gray-600">No events recorded yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white/[0.03] border-white/5 sm:col-span-2">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-semibold heading-font text-gray-200 mb-4">Cross-Chain Correlation</h3>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-bold heading-font mono text-blue-400">{analytics.cross_chain?.total_bridge_sent || 0}</p>
                        <p className="text-[10px] text-gray-500">Bridge Sends</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold heading-font mono text-emerald-400">{analytics.cross_chain?.correlated || 0}</p>
                        <p className="text-[10px] text-gray-500">Correlated</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold heading-font mono text-amber-400">{analytics.cross_chain?.unmatched || 0}</p>
                        <p className="text-[10px] text-gray-500">Unmatched</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-600 text-center mt-3">
                      OFTSent events are automatically correlated with OFTReceived events via LayerZero GUID
                    </p>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F7931A] mx-auto" /></div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
